/**
 * Application wrapper around the AWS Amplify Auth API.
 */
import { Auth, SignUpParams } from "@aws-amplify/auth";
import { ISignUpResult } from "amazon-cognito-identity-js";

import { CognitoUser } from "fond/types";

import { normaliseUsername } from "./username";

/**
 * Register a new user and email them a verification link.
 */
export function signUp(params: SignUpParams): Promise<ISignUpResult> {
  const username = normaliseUsername(params.username);
  return Auth.signUp({ ...params, username, attributes: { ...params.attributes, email: username } });
}

/**
 * Sign a user in.
 */
export function signIn(email: string, password: string): Promise<CognitoUser> {
  return Auth.signIn(normaliseUsername(email), password);
}

/**
 * Confirm a sign up using the code from the verification email.
 */
export function confirmSignUp(email: string, code: string): Promise<unknown> {
  return Auth.confirmSignUp(normaliseUsername(email), code);
}

/**
 * Resend the sign up verification email.
 */
export function resendSignUp(email: string): Promise<unknown> {
  return Auth.resendSignUp(normaliseUsername(email));
}

/**
 * Email the user a link for resetting their password.
 */
export function forgotPassword(email: string): Promise<unknown> {
  return Auth.forgotPassword(normaliseUsername(email));
}

/**
 * Set a new password using the code from the forgotten password email.
 */
export function forgotPasswordSubmit(email: string, code: string, password: string): Promise<string> {
  return Auth.forgotPasswordSubmit(normaliseUsername(email), code, password);
}

/**
 * The remaining Auth calls do not take a username string.
 */
export const configure: typeof Auth.configure = (config) => Auth.configure(config);
export const currentAuthenticatedUser: typeof Auth.currentAuthenticatedUser = (params) => Auth.currentAuthenticatedUser(params);
export const currentSession: typeof Auth.currentSession = () => Auth.currentSession();
export const completeNewPassword: typeof Auth.completeNewPassword = (user, password, requiredAttributes, clientMetadata) =>
  Auth.completeNewPassword(user, password, requiredAttributes, clientMetadata);
export const signOut: typeof Auth.signOut = (opts) => Auth.signOut(opts);
